import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Tooltip, VisuallyHidden } from "@mantine/core";
import { useReducedMotion } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconGripVertical, IconMessageCircle } from "@tabler/icons-react";
import type { UseObsScreenRecordingResult } from "@deep-consulting-solutions/observation-ui";

type DockSide = "left" | "right";

interface DockPosition {
  version: 1;
  side: DockSide;
  yRatio: number;
}

interface Point {
  left: number;
  top: number;
}

interface Size {
  width: number;
  height: number;
}

interface ViewportSize {
  width: number;
  height: number;
  offsetLeft: number;
  offsetTop: number;
}

interface ActiveDrag {
  pointerId: number;
  startX: number;
  startY: number;
  originLeft: number;
  originTop: number;
  latestLeft: number;
  latestTop: number;
  moved: boolean;
}

interface FloatingObservationTriggerProps {
  sessionId: string | null;
  recordingHook: UseObsScreenRecordingResult;
}

export const FLOATING_OBSERVATION_POSITION_KEY =
  "gcp:share-observation-position:v1";

const DEFAULT_POSITION: DockPosition = {
  version: 1,
  side: "left",
  yRatio: 0.62,
};

const DEFAULT_BUTTON_SIZE: Size = { width: 190, height: 44 };
const EDGE_GAP = 8;
const HEADER_SAFE_TOP = 68;
const DRAG_THRESHOLD_PX = 6;
const DRAG_CLICK_SUPPRESSION_MS = 750;
const KEYBOARD_STEP_PX = 24;
// Above app chrome/modals, but below Mantine popovers (300) and notifications (400).
const FLOATING_TRIGGER_Z_INDEX = 250;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

function getViewportSize(): ViewportSize {
  if (typeof window === "undefined") {
    return { width: 1024, height: 768, offsetLeft: 0, offsetTop: 0 };
  }

  const visualViewport = window.visualViewport;

  return {
    width: Math.max(
      1,
      visualViewport?.width ??
        window.innerWidth ??
        document.documentElement.clientWidth
    ),
    height: Math.max(
      1,
      visualViewport?.height ??
        window.innerHeight ??
        document.documentElement.clientHeight
    ),
    offsetLeft: Math.max(0, visualViewport?.offsetLeft ?? 0),
    offsetTop: Math.max(0, visualViewport?.offsetTop ?? 0),
  };
}

function getVerticalBounds(viewport: ViewportSize, buttonSize: Size) {
  const maxTop = Math.max(
    viewport.offsetTop + EDGE_GAP,
    viewport.offsetTop + viewport.height - buttonSize.height - EDGE_GAP
  );
  const minTop = Math.min(viewport.offsetTop + HEADER_SAFE_TOP, maxTop);

  return {
    minTop,
    maxTop,
    travel: Math.max(0, maxTop - minTop),
  };
}

function getHorizontalBounds(viewport: ViewportSize, buttonSize: Size) {
  const maxLeft = Math.max(
    viewport.offsetLeft + EDGE_GAP,
    viewport.offsetLeft + viewport.width - buttonSize.width - EDGE_GAP
  );

  return {
    minLeft: Math.min(viewport.offsetLeft + EDGE_GAP, maxLeft),
    maxLeft,
  };
}

function getDockedPoint(
  position: DockPosition,
  viewport: ViewportSize,
  buttonSize: Size
): Point {
  const { minTop, maxTop, travel } = getVerticalBounds(viewport, buttonSize);
  const { minLeft, maxLeft } = getHorizontalBounds(viewport, buttonSize);

  return {
    left: position.side === "left" ? minLeft : maxLeft,
    top: clamp(minTop + position.yRatio * travel, minTop, maxTop),
  };
}

function clampPoint(
  point: Point,
  viewport: ViewportSize,
  buttonSize: Size
): Point {
  const { minTop, maxTop } = getVerticalBounds(viewport, buttonSize);
  const { minLeft, maxLeft } = getHorizontalBounds(viewport, buttonSize);

  return {
    left: clamp(point.left, minLeft, maxLeft),
    top: clamp(point.top, minTop, maxTop),
  };
}

function pointToDockPosition(
  point: Point,
  viewport: ViewportSize,
  buttonSize: Size
): DockPosition {
  const clampedPoint = clampPoint(point, viewport, buttonSize);
  const { minTop, travel } = getVerticalBounds(viewport, buttonSize);
  const side: DockSide =
    clampedPoint.left + buttonSize.width / 2 <
    viewport.offsetLeft + viewport.width / 2
      ? "left"
      : "right";

  return {
    version: 1,
    side,
    yRatio: travel === 0 ? 0 : (clampedPoint.top - minTop) / travel,
  };
}

function readSavedPosition(): DockPosition {
  if (typeof window === "undefined") return DEFAULT_POSITION;

  try {
    const rawPosition = window.localStorage.getItem(
      FLOATING_OBSERVATION_POSITION_KEY
    );
    if (!rawPosition) return DEFAULT_POSITION;

    const candidate = JSON.parse(rawPosition) as Partial<DockPosition>;
    if (
      candidate.version !== 1 ||
      (candidate.side !== "left" && candidate.side !== "right") ||
      typeof candidate.yRatio !== "number" ||
      !Number.isFinite(candidate.yRatio)
    ) {
      return DEFAULT_POSITION;
    }

    return {
      version: 1,
      side: candidate.side,
      yRatio: clamp(candidate.yRatio, 0, 1),
    };
  } catch {
    return DEFAULT_POSITION;
  }
}

function savePosition(position: DockPosition) {
  try {
    window.localStorage.setItem(
      FLOATING_OBSERVATION_POSITION_KEY,
      JSON.stringify(position)
    );
  } catch {
    // Storage can be unavailable in locked-down/private browser contexts.
  }
}

function useViewportSize() {
  const [viewport, setViewport] = useState<ViewportSize>(getViewportSize);

  useEffect(() => {
    let animationFrame: number | null = null;

    const updateViewport = () => {
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const nextViewport = getViewportSize();
        setViewport((currentViewport) =>
          currentViewport.width === nextViewport.width &&
          currentViewport.height === nextViewport.height &&
          currentViewport.offsetLeft === nextViewport.offsetLeft &&
          currentViewport.offsetTop === nextViewport.offsetTop
            ? currentViewport
            : nextViewport
        );
      });
    };

    updateViewport();
    window.addEventListener("resize", updateViewport);
    window.visualViewport?.addEventListener("resize", updateViewport);
    window.visualViewport?.addEventListener("scroll", updateViewport);

    return () => {
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", updateViewport);
      window.visualViewport?.removeEventListener("resize", updateViewport);
      window.visualViewport?.removeEventListener("scroll", updateViewport);
    };
  }, []);

  return viewport;
}

/**
 * A globally mounted observation launcher that remains available independently
 * of the responsive sidebar. It can be dragged between the viewport edges and
 * stores a validated, viewport-independent vertical position.
 */
export default function FloatingObservationTrigger({
  sessionId,
  recordingHook,
}: FloatingObservationTriggerProps) {
  const {
    state: recordingState,
    isBrowserSupported,
    permissionError,
    start: startRecording,
    reset: resetRecording,
  } = recordingHook;
  const [dockPosition, setDockPosition] =
    useState<DockPosition>(readSavedPosition);
  const [freePosition, setFreePosition] = useState<Point | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [buttonSize, setButtonSize] = useState<Size>(DEFAULT_BUTTON_SIZE);
  const [buttonElement, setButtonElement] = useState<HTMLButtonElement | null>(
    null
  );
  const viewport = useViewportSize();
  const reduceMotion = useReducedMotion();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dockPositionRef = useRef(dockPosition);
  const viewportRef = useRef(viewport);
  const buttonSizeRef = useRef(buttonSize);
  const activeDragRef = useRef<ActiveDrag | null>(null);
  const suppressClickUntilRef = useRef(0);
  const launchGuardRef = useRef(false);
  const restoreFocusRef = useRef(false);
  const previousRecordingStateRef = useRef(recordingState);
  const instructionsId = useId();

  dockPositionRef.current = dockPosition;
  viewportRef.current = viewport;
  buttonSizeRef.current = buttonSize;

  const handleButtonRef = useCallback((button: HTMLButtonElement | null) => {
    buttonRef.current = button;
    setButtonElement(button);
  }, []);

  const commitDockPosition = useCallback((position: DockPosition) => {
    const validatedPosition = {
      ...position,
      yRatio: clamp(position.yRatio, 0, 1),
    };
    dockPositionRef.current = validatedPosition;
    setDockPosition(validatedPosition);
    savePosition(validatedPosition);
  }, []);

  useLayoutEffect(() => {
    const button = buttonElement;
    if (!button) return undefined;

    const measure = () => {
      const rect = button.getBoundingClientRect();
      const measuredWidth = rect.width || button.offsetWidth;
      const measuredHeight = rect.height || button.offsetHeight;
      if (!measuredWidth || !measuredHeight) return;

      setButtonSize((currentSize) =>
        currentSize.width === measuredWidth &&
        currentSize.height === measuredHeight
          ? currentSize
          : { width: measuredWidth, height: measuredHeight }
      );
    };

    measure();
    if (typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(measure);
    observer.observe(button);
    return () => observer.disconnect();
  }, [buttonElement]);

  useEffect(() => {
    setFreePosition((currentPosition) =>
      currentPosition
        ? clampPoint(currentPosition, viewport, buttonSize)
        : currentPosition
    );
  }, [buttonSize, viewport]);

  useEffect(() => {
    const previousState = previousRecordingStateRef.current;
    const currentState = recordingState;

    if (
      currentState === "error" ||
      currentState === "canceled" ||
      (currentState === "idle" && previousState !== "idle")
    ) {
      launchGuardRef.current = false;
    }

    previousRecordingStateRef.current = currentState;
  }, [recordingState]);

  useEffect(() => {
    if (
      !restoreFocusRef.current ||
      (recordingState !== "idle" &&
        recordingState !== "error" &&
        recordingState !== "canceled")
    ) {
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      buttonRef.current?.focus({ preventScroll: true });
      restoreFocusRef.current = false;
    });
    return () => window.cancelAnimationFrame(animationFrame);
  }, [recordingState]);

  useEffect(() => {
    if (recordingState !== "error" || !permissionError) {
      return undefined;
    }

    notifications.show({
      id: "gcp-observation-recording-error",
      color: "red",
      title: "Could not start observation recording",
      message: permissionError,
      autoClose: 3000,
    });
    const timer = window.setTimeout(() => resetRecording(), 3000);
    return () => window.clearTimeout(timer);
  }, [permissionError, recordingState, resetRecording]);

  useEffect(() => {
    if (recordingState !== "canceled") return undefined;

    notifications.show({
      id: "gcp-observation-recording-canceled",
      color: "orange",
      title: "Recording canceled",
      message: "No observation was submitted.",
      autoClose: 3000,
    });
    const timer = window.setTimeout(() => resetRecording(), 3000);
    return () => window.clearTimeout(timer);
  }, [recordingState, resetRecording]);

  const dockedPoint = useMemo(
    () => getDockedPoint(dockPosition, viewport, buttonSize),
    [buttonSize, dockPosition, viewport]
  );
  const displayedPoint = freePosition ?? dockedPoint;

  const finishDrag = useCallback(
    (pointerId: number, suppressClick: boolean) => {
      const activeDrag = activeDragRef.current;
      if (!activeDrag || activeDrag.pointerId !== pointerId) return;

      activeDragRef.current = null;
      if (activeDrag.moved) {
        const snappedPosition = pointToDockPosition(
          { left: activeDrag.latestLeft, top: activeDrag.latestTop },
          viewportRef.current,
          buttonSizeRef.current
        );
        commitDockPosition(snappedPosition);
        setFreePosition(null);
        setIsDragging(false);

        if (suppressClick) {
          suppressClickUntilRef.current =
            Date.now() + DRAG_CLICK_SUPPRESSION_MS;
        }
      }
    },
    [commitDockPosition]
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (
        activeDragRef.current ||
        event.isPrimary === false ||
        (event.pointerType === "mouse" && event.button !== 0)
      ) {
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const origin =
        rect.width > 0 && rect.height > 0
          ? { left: rect.left, top: rect.top }
          : getDockedPoint(
              dockPositionRef.current,
              viewportRef.current,
              buttonSizeRef.current
            );

      activeDragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originLeft: origin.left,
        originTop: origin.top,
        latestLeft: origin.left,
        latestTop: origin.top,
        moved: false,
      };

      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Window-level listeners below complete the drag if capture is absent.
      }
    },
    []
  );

  const moveActiveDrag = useCallback(
    (
      pointerId: number,
      clientX: number,
      clientY: number,
      preventDefault: () => void
    ) => {
      const activeDrag = activeDragRef.current;
      if (
        !activeDrag ||
        activeDrag.pointerId !== pointerId ||
        !Number.isFinite(clientX) ||
        !Number.isFinite(clientY)
      ) {
        return;
      }

      const deltaX = clientX - activeDrag.startX;
      const deltaY = clientY - activeDrag.startY;
      if (!activeDrag.moved && Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD_PX) {
        return;
      }

      preventDefault();
      activeDrag.moved = true;
      const nextPoint = clampPoint(
        {
          left: activeDrag.originLeft + deltaX,
          top: activeDrag.originTop + deltaY,
        },
        viewportRef.current,
        buttonSizeRef.current
      );
      activeDrag.latestLeft = nextPoint.left;
      activeDrag.latestTop = nextPoint.top;
      setIsDragging(true);
      setFreePosition(nextPoint);
    },
    []
  );

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      moveActiveDrag(event.pointerId, event.clientX, event.clientY, () =>
        event.preventDefault()
      );
    };
    const handlePointerUp = (event: PointerEvent) => {
      moveActiveDrag(event.pointerId, event.clientX, event.clientY, () => {});
      try {
        buttonRef.current?.releasePointerCapture(event.pointerId);
      } catch {
        // The browser may already have released capture.
      }
      finishDrag(event.pointerId, true);
    };
    const handlePointerCancel = (event: PointerEvent) => {
      finishDrag(event.pointerId, false);
    };
    const handleWindowBlur = () => {
      const activeDrag = activeDragRef.current;
      if (activeDrag) finishDrag(activeDrag.pointerId, false);
    };

    window.addEventListener("pointermove", handlePointerMove, {
      passive: false,
    });
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerCancel);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerCancel);
      window.removeEventListener("blur", handleWindowBlur);
      activeDragRef.current = null;
    };
  }, [finishDrag, moveActiveDrag]);

  const handleClick = useCallback(() => {
    if (Date.now() <= suppressClickUntilRef.current) {
      suppressClickUntilRef.current = 0;
      return;
    }
    if (launchGuardRef.current) return;

    if (!isBrowserSupported) {
      notifications.show({
        id: "gcp-observation-browser-unsupported",
        color: "gray",
        title: "Screen recording is not supported",
        message:
          "Please use a current version of Chrome or Edge to share an observation.",
        autoClose: 5000,
      });
      return;
    }

    if (
      recordingState !== "idle" &&
      recordingState !== "error" &&
      recordingState !== "canceled"
    ) {
      return;
    }

    // Keep start() directly in the user gesture. In particular, do not await,
    // reset state, or close navigation before this call: Safari requires the
    // getDisplayMedia request to remain in the synchronous activation chain.
    launchGuardRef.current = true;
    restoreFocusRef.current = true;
    try {
      const startRequest = startRecording(sessionId);
      void startRequest.catch(() => {
        launchGuardRef.current = false;
        notifications.show({
          id: "gcp-observation-recording-error",
          color: "red",
          title: "Could not start observation recording",
          message:
            "Please try again or use a current version of Chrome or Edge.",
          autoClose: 3000,
        });
      });
    } catch {
      launchGuardRef.current = false;
      notifications.show({
        id: "gcp-observation-recording-error",
        color: "red",
        title: "Could not start observation recording",
        message: "Please try again or use a current version of Chrome or Edge.",
        autoClose: 3000,
      });
    }
  }, [isBrowserSupported, recordingState, sessionId, startRecording]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      const { minTop, maxTop, travel } = getVerticalBounds(
        viewport,
        buttonSize
      );
      const currentTop = getDockedPoint(
        dockPositionRef.current,
        viewport,
        buttonSize
      ).top;
      const step = event.shiftKey ? KEYBOARD_STEP_PX * 3 : KEYBOARD_STEP_PX;
      let nextPosition: DockPosition | null = null;

      switch (event.key) {
        case "ArrowLeft":
          nextPosition = { ...dockPositionRef.current, side: "left" };
          break;
        case "ArrowRight":
          nextPosition = { ...dockPositionRef.current, side: "right" };
          break;
        case "ArrowUp":
          nextPosition = {
            ...dockPositionRef.current,
            yRatio:
              travel === 0
                ? 0
                : (clamp(currentTop - step, minTop, maxTop) - minTop) / travel,
          };
          break;
        case "ArrowDown":
          nextPosition = {
            ...dockPositionRef.current,
            yRatio:
              travel === 0
                ? 0
                : (clamp(currentTop + step, minTop, maxTop) - minTop) / travel,
          };
          break;
        case "Home":
          nextPosition = { ...dockPositionRef.current, yRatio: 0 };
          break;
        case "End":
          nextPosition = { ...dockPositionRef.current, yRatio: 1 };
          break;
        default:
          break;
      }

      if (!nextPosition) return;
      event.preventDefault();
      commitDockPosition(nextPosition);
    },
    [buttonSize, commitDockPosition, viewport]
  );

  const isBusy =
    recordingState !== "idle" &&
    recordingState !== "error" &&
    recordingState !== "canceled";

  return (
    <>
      <VisuallyHidden id={instructionsId}>
        Activate to begin sharing an observation. Drag to move it along either
        edge. Use Left or Right Arrow to change edges, Up or Down Arrow to move,
        and Home or End to jump vertically.
      </VisuallyHidden>
      {!isBusy && (
        <Tooltip
          label="Click to share an observation. Drag to dock on either side."
          position={dockPosition.side === "left" ? "right" : "left"}
          withArrow
          openDelay={500}
          disabled={isDragging}
          events={{ hover: true, focus: true, touch: false }}
          withinPortal
          zIndex={FLOATING_TRIGGER_Z_INDEX + 1}
        >
          <Button
            ref={handleButtonRef}
            type="button"
            aria-label="Share Observation"
            aria-describedby={instructionsId}
            data-testid="share-observation-floating-trigger"
            data-dock-side={dockPosition.side}
            leftSection={<IconMessageCircle size={18} stroke={1.7} />}
            rightSection={<IconGripVertical size={16} stroke={1.7} />}
            color="gcpBlue"
            radius="xl"
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onPointerDown={handlePointerDown}
            style={{
              position: "fixed",
              left: displayedPoint.left,
              top: displayedPoint.top,
              zIndex: FLOATING_TRIGGER_Z_INDEX,
              width: Math.min(
                DEFAULT_BUTTON_SIZE.width,
                Math.max(1, viewport.width - EDGE_GAP * 2)
              ),
              height: DEFAULT_BUTTON_SIZE.height,
              touchAction: "none",
              userSelect: "none",
              WebkitUserSelect: "none",
              cursor: isDragging ? "grabbing" : "grab",
              boxShadow: "0 6px 20px rgba(4, 17, 154, 0.28)",
              transition:
                isDragging || reduceMotion
                  ? "none"
                  : "left 160ms ease, top 160ms ease, box-shadow 160ms ease",
            }}
          >
            Share Observation
          </Button>
        </Tooltip>
      )}
    </>
  );
}
